<template>
  <div class="h-full flex flex-col" style="background: var(--background-primary);">
    <div v-if="section" class="flex-1 flex flex-col overflow-hidden">
      <!-- Content Header -->
      <div class="border-b px-6 py-4" style="border-color: var(--border-light);">
        <div class="flex items-center gap-3">
          <i class="pi pi-file text-xl" style="color: var(--primary-gold);"></i>
          <div class="flex-1">
            <h2 class="text-2xl font-semibold" style="color: var(--text-primary);">
              {{ section.title }}
            </h2>
            <p v-if="section.estimated_read_time" class="text-sm mt-1 flex items-center gap-1.5" style="color: var(--text-muted);">
              <i class="pi pi-clock"></i>
              <span>{{ section.estimated_read_time }}</span>
            </p>
          </div>
        </div>
      </div>

      <!-- Content Area: Framework Overview with tabs for section 0.1, -->
      <TabView v-if="isOverviewSection" class="flex-1 overflow-hidden explorer-tabs" @tab-change="handleTabChange">
        <!-- Domain Overview (0.1) - Dynamic tabs based on tab_contents -->
        <TabPanel v-if="hasTab('overview')" header="Overview">
            <OverviewTab :section="section" tab-name="overview" />
          </TabPanel>

        <TabPanel v-if="hasTab('definitions')" header="Key Definitions">
            <DefinitionsTab :section="section" />
          </TabPanel>

        <TabPanel v-if="hasTab('regulations')" header="Regulations & Compliance">
            <OverviewTab :section="section" tab-name="regulations" />
          </TabPanel>

        <TabPanel v-if="hasTab('technology')" header="Technology">
            <OverviewTab :section="section" tab-name="technology" />
          </TabPanel>

      </TabView> 
      

      <!-- Tab Navigation for all other sections -->
      <TabView v-else class="flex-1 overflow-hidden explorer-tabs" @tab-change="handleTabChange">
        <!-- Framework Domain: Dynamic tabs based on section metadata -->
        <template v-if="domain === 'framework'">
          <TabPanel v-if="hasTab('overview')" header="Overview">
            <OverviewTab :section="section" tab-name="overview" />
          </TabPanel>

          <TabPanel v-if="hasTab('regulations')" header="Regulations & Compliance">
            <OverviewTab :section="section" tab-name="regulations" />
          </TabPanel>

          <TabPanel v-if="hasTab('technology')" header="Technology">
            <OverviewTab :section="section" tab-name="technology" />
          </TabPanel>

          <TabPanel v-if="hasTab('related-policies')" header="Related Policies">
            <RelatedPoliciesTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('related-processes')" header="Related Processes">
            <RelatedProcessesTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('comparison')" header="Comparison">
            <ComparisonTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('examples')" header="Examples">
            <ExamplesTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('definition')" header="Definition">
            <ExamplesTab :section="section" />
          </TabPanel>

        </template>

        <!-- Policy Domain: Dynamic tabs based on navigation.json -->
        <template v-else-if="domain === 'policy'">
          <TabPanel v-if="hasTab('overview')" header="Overview">
            <OverviewTab :section="section" tab-name="overview" />
          </TabPanel>

          <TabPanel v-if="hasTab('template-guide')" header="Template Guide">
            <TemplateGuideTab :section="section" @show-template="handleShowTemplate" />
          </TabPanel>

          <TabPanel v-if="hasTab('how-to-use')" header="How to Use">
            <HowToUseTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('resources')" header="Resources">
            <ResourcesTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('related-processes')" header="Related Processes">
            <RelatedProcessesTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('iso-controls')" header="Controls">
            <ExemplarControlsTab :section="section" />
          </TabPanel>
        </template>

        <!-- Process Domain: Dynamic tabs based on navigation.json -->
        <template v-else-if="domain === 'process'">
          <TabPanel v-if="hasTab('overview')" header="Overview">
            <OverviewTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('base-practices')" header="Base Practices">
            <BasePracticesTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('sops')" header="SOPs">
            <SopsTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('related-policies')" header="Related Policies">
            <RelatedPoliciesTab :section="section" />
          </TabPanel>

          <TabPanel v-if="hasTab('cross-domain')" header="Cross-Domain Integration">
            <DependenciesTab :section="section" />
          </TabPanel>
        </template>
      </TabView>
    </div>

    <!-- Empty State -->
    <div v-else class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <i class="pi pi-arrow-left text-4xl mb-4" style="color: var(--text-muted);"></i>
        <p class="text-lg" style="color: var(--text-secondary);">Select a section to view details</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import OverviewTab from './tabs/OverviewTab.vue';
import YamlTab from './tabs/YamlTab.vue';
import BasePracticesTab from './tabs/BasePracticesTab.vue';
import SopsTab from './tabs/SopsTab.vue';
import DependenciesTab from './tabs/DependenciesTab.vue';
import WorkProductsTab from './tabs/WorkProductsTab.vue';
// Framework-specific tabs
import RelatedPoliciesTab from './tabs/RelatedPoliciesTab.vue';
import RelatedProcessesTab from './tabs/RelatedProcessesTab.vue';
import ComparisonTab from './tabs/ComparisonTab.vue';
import ExamplesTab from './tabs/ExamplesTab.vue';
import DefinitionsTab from './tabs/DefinitionsTab.vue';
// Policy-specific tabs
import TemplateGuideTab from './tabs/TemplateGuideTab.vue';
import HowToUseTab from './tabs/HowToUseTab.vue';
import ResourcesTab from './tabs/ResourcesTab.vue';
import ExemplarControlsTab from './tabs/ExemplarControlsTab.vue';
import type { Section, DomainType } from '@/types/ashmatics';

const props = defineProps<{
  section: Section | null;
  domain: DomainType;
}>();

const emit = defineEmits<{
  'active-content-changed': [content: string, showToc?: boolean];
  'show-template': [section: Section];
}>();

const activeTabIndex = ref(0);

/**
 * Handle template preview request from TemplateGuideTab
 */
const handleShowTemplate = () => {
  if (props.section) {
    emit('show-template', props.section);
  }
};

/**
 * Check if current section is the domain overview (section 0.1)
 * These sections should display with the special overview TabView
 */
const isOverviewSection = computed(() => {
  return props.section?.id === '0.1';
});

/**
 * Check if a tab should be displayed for the current section
 * Based on the tabs array in the section metadata
 */
const hasTab = (tabName: string): boolean => {
  if (!props.section?.tabs) return false;
  return props.section.tabs.includes(tabName);
};

/**
 * Handle tab change and emit the markdown content of the active tab
 */
const handleTabChange = (event: { index: number }) => {
  activeTabIndex.value = event.index;
  emitActiveContent();
};

/**
 * Tabs that are component-driven and should not show TOC
 * These tabs render custom components rather than markdown content
 */
const componentDrivenTabs = [
  'related-processes',
  'related-policies',
  'iso-controls',
  'exemplar-controls',
  'base-practices',
  'sops',
  'cross-domain'
];

/**
 * Emit the markdown content of the current active tab
 * Also emits whether TOC should be shown (false for component-driven tabs)
 */
const emitActiveContent = () => {
  if (!props.section) return;

  // For domains with tab_contents (Framework and Policy)
  if (props.section.tab_contents && (props.domain === 'framework' || props.domain === 'policy')) {
    const tabNames = props.section.tabs || [];
    const activeTabName = tabNames[activeTabIndex.value];

    // Check if this is a component-driven tab (no TOC needed)
    const showToc = !componentDrivenTabs.includes(activeTabName);

    // For component-driven tabs, always emit with empty content and showToc=false
    if (componentDrivenTabs.includes(activeTabName)) {
      emit('active-content-changed', '', false);
      return;
    }

    // For content tabs, emit the actual content with showToc=true
    if (activeTabName && props.section.tab_contents[activeTabName]) {
      emit('active-content-changed', props.section.tab_contents[activeTabName], true);
      return;
    }
  }

  // Fallback to markdown_content (show TOC by default)
  emit('active-content-changed', props.section.markdown_content || '', true);
};

// Watch for section changes to reset tab index and emit content
watch(() => props.section, () => {
  activeTabIndex.value = 0;
  emitActiveContent();
}, { immediate: true });
</script>

<style scoped>
.explorer-tabs :deep(.p-tabview-panels) {
  height: calc(100% - 4rem);
  overflow-y: auto;
  background: var(--background-primary);
}

.explorer-tabs :deep(.p-tabview-nav) {
  background: var(--background-primary) !important;
  flex-shrink: 0;
}

.explorer-tabs :deep(.p-tabview) {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
